import {
  useRef,
  useMemo,
  useSyncExternalStore,
  type CSSProperties,
  type ReactNode,
  type Ref,
} from "react";
import styled from "styled-components";

import { Spinner } from "../Spinner";
import {
  DataTableAppendFooter,
  useDataTableAppend,
  type DataTableAppendMessages,
} from "../DataTable/appendLoading";
import { Checkbox } from "../Checkbox";
import { Scrollbar } from "../Scrollbar";
import { Tooltip } from "../Tooltip";
import { CollectionMessage } from "../DataTable/collectionMessage";
import { useRowActivation } from "../DataTable/rowActivation";
import { SELECT_CHECKBOX_SIZE } from "../DataTable/constants";
import { canToggleRowSelection, resolveSelectAllState } from "../DataTable/DataTableSource";
import type { DataTableSource, TableRow } from "../DataTable";

export type DataGridViewCellSize = "auto" | string;

export type DataGridViewProps<Row extends TableRow> = {
  source: DataTableSource<Row>;
  renderItem: (row: Row) => ReactNode;
  cellWidth?: DataGridViewCellSize;
  cellHeight?: DataGridViewCellSize;
  /** 빈 결과. 글자는 c6 가 문단으로 그리고, 요소 노드는 문단·여백·면 없이 그대로 둔다(배치는 노드 소유). */
  emptyMessage?: ReactNode;
  /** 첫 조회의 대기 자리. 조회 전(idle)에도 그린다. 글자와 노드의 구분은 `emptyMessage` 와 같다. */
  loadingMessage?: ReactNode;
  /**
   * 페이지 모드에서 첫 조회가 실패하고 행이 없을 때의 본문. 생략하면 source snapshot 의
   * `errorMessage` 를 글자로 그린다. 글자와 노드의 구분은 `emptyMessage` 와 같다.
   * append 모드는 이 값을 쓰지 않는다. 그 실패는 footer 가 알린다.
   */
  errorMessage?: ReactNode;
  appendMessages?: DataTableAppendMessages;
  ariaLabel?: string;
  className?: string;
  scrollable?: boolean;
  // true면 각 카드 좌상단에 선택 체크박스를 얹는다. 선택 상태는 source가 소유.
  showCheckbox?: boolean;
  // 전체선택 체크박스 노출(기본 true — showCheckbox와 함께 켜진다). 카드 단위 선택만 두고
  // 한 번에 페이지 전체를 담는 어포던스는 빼는 소비처가 있어 showCheckbox와 분리한다.
  showSelectAll?: boolean;
  // 전체선택 체크박스의 이름 — 상시 텍스트가 아니라 hover 툴팁 + aria-label로 노출한다
  // (tooltip-as-label, TooltipIconButton 관례). emptyMessage처럼 소비처 로케일에 맞게 교체한다.
  selectAllLabel?: string;
};

// 표현 계층: 카드 그리드 컨테이너·아이템·empty 스타일
const Shell = styled.div`
  display: grid;
  min-width: 0;
  min-height: 0;

  /* 전체선택 바와 콘텐츠 사이 간격. 바가 없으면(자식 1개) gap은 적용되지 않는다. */
  row-gap: 12px;

  &[data-scrollable="true"] {
    grid-template-rows: minmax(0, 1fr);
    overflow: hidden;
  }

  /* 전체선택 바는 스크롤 영역 밖 고정 행으로 둔다(표의 sticky 헤더와 같은 체류 규칙). */
  &[data-scrollable="true"][data-select-all="true"] {
    grid-template-rows: auto minmax(0, 1fr);
  }

  .content {
    min-width: 0;
    min-height: 0;
  }

  .grid {
    display: grid;
    min-width: 0;
    align-items: stretch;
    gap: 16px;
  }

  .item {
    position: relative;
    min-width: 0;
    min-height: 0;
  }

  .clickableItem {
    cursor: pointer;
  }

  .clickableItem:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.border.focus};
    outline-offset: 3px;
  }

  /* 선택 체크박스 오버레이(좌상단). 카드 활성화와 분리하려 클릭 전파는 span에서 끊는다. */
  .selectOverlay {
    position: absolute;
    z-index: 1;
    top: 8px;
    left: 8px;
  }

  /* 그리드 위 전체선택 바. 좌 8px 는 카드 체크박스 오버레이(inset 8px)와 열 맞춤, 위 4px 는
     셸이 overflow:hidden 이라 맨 위 행 체크박스의 포커스 링(2px ring + offset)이 경계에
     깎이지 않게 두는 클리어런스다. flex 로 두어 line box 를 만들지 않는다: block 이면
     inline-level 트리거의 baseline 이 바 높이에 섞여, 전체선택 체크 상태에 따라 바가 4px
     커졌다 작아지며 그리드가 튄다(PIT-state-dependent-inline-baseline. 표의 .selectCell 과 같은 관례). */
  .selectAllBar {
    display: flex;
    align-items: center;
    padding: 4px 0 0 8px;
  }

  /* 선택 시각은 소비처가 소유한다: 선택된 아이템 래퍼에 data-selected를 달아, renderItem의 카드가
     자기 테두리를 직접 강조하게 한다(래퍼 outline은 스크롤 컨테이너에 잘리고 카드와 분리돼 뜬다). */

  .empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 14rem;
    padding: 48px;
    border: 1px solid ${({ theme }) => theme.color.border.default};
    border-radius: ${({ theme }) => theme.shape.radius.surface};
    background: ${({ theme }) => theme.color.bg.surface};
    color: ${({ theme }) => theme.color.text.muted};
    font-size: ${({ theme }) => theme.typography.size.body};
  }
`;

// 표현 계층: DataGridView — DataTableSource를 auto-fill 카드 그리드로 렌더(정렬·재정렬 없음)
export function DataGridView<Row extends TableRow>({
  source,
  renderItem,
  cellWidth = "18rem",
  cellHeight = "auto",
  emptyMessage = "No data.",
  loadingMessage = "Loading.",
  errorMessage,
  appendMessages,
  ariaLabel,
  className,
  scrollable = false,
  showCheckbox = false,
  showSelectAll = true,
  selectAllLabel = source.paginationMode === "append" ? "Select all loaded items" : "Select all",
}: DataGridViewProps<Row>) {
  const sourceSnapshot = useSyncExternalStore(
    source.subscribe,
    source.getSnapshot,
    source.getSnapshot,
  );
  const scrollerRef = useRef<HTMLDivElement>(null);
  const { sentinelRef, requestMore } = useDataTableAppend(
    source,
    sourceSnapshot,
    scrollerRef,
    scrollable,
  );
  const rows = sourceSnapshot.rows;
  const selectedIds = sourceSnapshot.selectedIds;
  // 첫 조회 전(idle)도 대기다. 소비처는 마운트 뒤에 initialize() 를 부른다. 그 전의 페인트에 빈 결과
  // 문구를 그리면 조회하지 않은 목록을 "없다"고 알린다.
  const isInitialLoading =
    (sourceSnapshot.loadState === "loading" || sourceSnapshot.loadState === "idle") &&
    rows.length === 0;
  // 그리드엔 표 헤더가 없어 전체선택을 그리드 위 바로 노출한다. 대상·checked·indeterminate는
  // 표 헤더와 같은 공유 계산(resolveSelectAllState)이 소유한다.
  const showSelectAllCheckbox = showCheckbox && showSelectAll && rows.length > 0;
  const {
    selectableRows,
    allSelected: allRowsSelected,
    someSelected: someRowsSelected,
  } = useMemo(
    () =>
      showSelectAllCheckbox
        ? resolveSelectAllState(rows, selectedIds, source)
        : { selectableRows: [] as readonly Row[], allSelected: false, someSelected: false },
    [rows, selectedIds, showSelectAllCheckbox, source],
  );
  const gridStyle = useMemo<CSSProperties>(
    () => ({
      gridTemplateColumns: resolveGridTemplateColumns(cellWidth),
    }),
    [cellWidth],
  );
  const itemStyle = useMemo<CSSProperties>(
    () => (cellHeight === "auto" ? {} : { height: cellHeight }),
    [cellHeight],
  );
  const { onActivateClick, onActivateKeyDown } = useRowActivation<Row>();

  // 렌더: 로드 상태(loading/failed/empty) + auto-fill 카드 그리드(clickable이면 role=button)
  return (
    <Shell
      className={className}
      aria-label={ariaLabel}
      aria-busy={
        sourceSnapshot.append ? isInitialLoading || sourceSnapshot.append.loadingMore : undefined
      }
      data-scrollable={scrollable ? "true" : "false"}
      data-select-all={showSelectAllCheckbox ? "true" : "false"}
    >
      {showSelectAllCheckbox ? (
        <div className="selectAllBar">
          {/* 라벨은 상시 텍스트 대신 hover 툴팁으로 노출한다 — 체크박스가 줄의 유일한
              콘텐츠라 바가 얇아지고, 접근 이름은 같은 문자열의 aria-label 이 소유한다
              (describeTrigger=false 로 이중 낭독을 막는다 — TooltipIconButton 관례). */}
          <Tooltip content={selectAllLabel} describeTrigger={false}>
            <Checkbox
              checked={allRowsSelected}
              indeterminate={someRowsSelected && !allRowsSelected}
              disabled={selectableRows.length === 0}
              onChange={(event) => source.setRowsSelected(selectableRows, event.target.checked)}
              aria-label={selectAllLabel}
              size={SELECT_CHECKBOX_SIZE}
            />
          </Tooltip>
        </div>
      ) : null}
      <DataGridViewContent scrollable={scrollable} scrollerRef={scrollerRef}>
        {isInitialLoading ? (
          <CollectionMessage leading={sourceSnapshot.append ? <Spinner size="sm" /> : null}>
            {loadingMessage}
          </CollectionMessage>
        ) : sourceSnapshot.loadState === "failed" && rows.length === 0 ? (
          sourceSnapshot.append ? null : (
            <CollectionMessage>
              {errorMessage ?? sourceSnapshot.errorMessage ?? emptyMessage}
            </CollectionMessage>
          )
        ) : rows.length === 0 ? (
          <CollectionMessage>{emptyMessage}</CollectionMessage>
        ) : (
          <div className="grid" style={gridStyle} role="list">
            {rows.map((row, rowIndex) => {
              const selected = showCheckbox && selectedIds.has(row.getRowId());
              return (
                <div
                  key={row.getRowId()}
                  className={resolveItemClassName(row)}
                  data-selected={selected ? "true" : undefined}
                  style={itemStyle}
                  role={row.isRowClickable ? "button" : "listitem"}
                  tabIndex={row.isRowClickable ? 0 : undefined}
                  onFocus={
                    sourceSnapshot.append && rowIndex === rows.length - 1 ? requestMore : undefined
                  }
                  onClick={row.isRowClickable ? (event) => onActivateClick(event, row) : undefined}
                  onKeyDown={
                    row.isRowClickable ? (event) => onActivateKeyDown(event, row) : undefined
                  }
                >
                  {showCheckbox ? (
                    // 체크박스 클릭이 카드 활성화(상세 열기)로 흡수되지 않게 전파만 끊는다. 토글은
                    // Checkbox가 담당하므로 오버레이 자체는 키보드 인터랙션 대상이 아니다.
                    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                    <span className="selectOverlay" onClick={(event) => event.stopPropagation()}>
                      <Checkbox
                        checked={selected}
                        // 선택된 카드는 항상 해제할 수 있다 — 판정은 전체선택 대상과 같은 헬퍼가 소유.
                        disabled={!canToggleRowSelection(row, selectedIds, source)}
                        onChange={() => source.toggleRowSelection(row)}
                        aria-label="Select item"
                        size={SELECT_CHECKBOX_SIZE}
                      />
                    </span>
                  ) : null}
                  {renderItem(row)}
                </div>
              );
            })}
          </div>
        )}
        <DataTableAppendFooter
          source={source}
          snapshot={sourceSnapshot}
          sentinelRef={sentinelRef}
          messages={appendMessages}
        />
      </DataGridViewContent>
    </Shell>
  );
}

// 내부 헬퍼: 스크롤 래퍼·그리드 트랙 계산·아이템 className
function DataGridViewContent({
  children,
  scrollable,
  scrollerRef,
}: {
  children: ReactNode;
  scrollable: boolean;
  scrollerRef: Ref<HTMLDivElement>;
}) {
  if (scrollable) {
    // 세로 스크롤바가 없을 때(짧은 목록) 카드 그리드 우측에 빈 gutter 띠가 남지 않도록 auto.
    return (
      <Scrollbar className="content" gutter="auto" ref={scrollerRef}>
        {children}
      </Scrollbar>
    );
  }

  return (
    <div className="content" ref={scrollerRef}>
      {children}
    </div>
  );
}

export function resolveGridTemplateColumns(cellWidth: DataGridViewCellSize): string {
  if (cellWidth === "auto") {
    return "1fr";
  }
  return `repeat(auto-fill, minmax(min(100%, ${cellWidth}), 1fr))`;
}

function resolveItemClassName(row: TableRow): string {
  return ["item", row.isRowClickable ? "clickableItem" : ""].filter(Boolean).join(" ");
}
